import React, { useMemo, useRef, useEffect, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import useDefaultMapConfig from '../hooks/useDefaultMapConfig';
import useHeatmapConfig from '../hooks/useHeatmapConfig';
import { createHeatmapOverlay } from '../utils/canvasHeatmapOverlay';
import { reportsToHeatmapPoints, gradientLegendSegments } from '../utils/heatmapUtils';

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDfJ-ekd6yt6eT6G2lkRDE4-R5rpxq1jLU';
const GOOGLE_MAPS_LIBRARIES = [];
const DEFAULT_CENTER = { lat: -37.7354797, lng: 144.9727395 };

function IncidentHeatmap({
  reports,
  typeFilter = 'all',
  dateRange = { from: '', to: '' },
  includePending = false,
  radius = 40,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    version: 'weekly',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const { mapRestrictions } = useDefaultMapConfig();
  const { config: heatmapConfig, loading: heatmapConfigLoading } = useHeatmapConfig();
  const [mapRef, setMapRef] = useState(null);
  const heatmapRef = useRef(null);
  const [heatmapRadius, setHeatmapRadius] = useState(radius);
  const heatmapDefaultsApplied = useRef(false);

  useEffect(() => {
    if (heatmapConfigLoading || heatmapDefaultsApplied.current) return;
    setHeatmapRadius(heatmapConfig.defaultRadius);
    heatmapDefaultsApplied.current = true;
  }, [heatmapConfigLoading, heatmapConfig.defaultRadius]);

  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      if (!includePending && !r.isApproved) return false;
      if (typeFilter !== 'all' && r.incidentType !== typeFilter) return false;
      if (dateRange.from && new Date(r.estimatedIncidentTime) < new Date(dateRange.from)) {
        return false;
      }
      if (dateRange.to && new Date(r.estimatedIncidentTime) > new Date(dateRange.to)) {
        return false;
      }
      return parseReportHasCoordinates(r);
    });
  }, [reports, typeFilter, dateRange, includePending]);

  const heatmapPoints = useMemo(() => reportsToHeatmapPoints(filteredReports), [filteredReports]);

  useEffect(() => {
    setHeatmapRadius(radius);
  }, [radius]);

  useEffect(() => {
    if (!isLoaded || !mapRef || heatmapConfigLoading) return undefined;

    if (heatmapPoints.length === 0) {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
      return undefined;
    }

    if (!heatmapRef.current) {
      heatmapRef.current = createHeatmapOverlay(mapRef, {
        points: heatmapPoints,
        radius: heatmapRadius,
        opacity: heatmapConfig.opacity,
        gradient: heatmapConfig.gradient,
      });
    } else {
      heatmapRef.current.setData(heatmapPoints);
      heatmapRef.current.setRadius(heatmapRadius);
      heatmapRef.current.setOpacity(heatmapConfig.opacity);
      heatmapRef.current.setGradient(heatmapConfig.gradient);
    }

    const bounds = new window.google.maps.LatLngBounds();
    heatmapPoints.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    mapRef.fitBounds(bounds, 50);

    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    };
  }, [isLoaded, mapRef, heatmapPoints, heatmapRadius, heatmapConfig, heatmapConfigLoading]);

  useEffect(() => {
    return () => {
      if (heatmapRef.current) {
        heatmapRef.current.setMap(null);
        heatmapRef.current = null;
      }
    };
  }, []);

  const legendSegments = useMemo(
    () => gradientLegendSegments(heatmapConfig.gradient),
    [heatmapConfig.gradient],
  );

  if (loadError) {
    return (
      <div className="w-full h-96 bg-red-50 rounded-xl flex items-center justify-center">
        <div className="text-red-600">Error loading Google Maps: {loadError.message}</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-xl flex items-center justify-center">
        <div className="text-gray-500">Loading map...</div>
      </div>
    );
  }

  if (heatmapPoints.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-xl flex items-center justify-center">
        <div className="text-gray-500">No incident data available for heatmap visualization</div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl border border-gray-200">
      <div className="w-full h-96 overflow-hidden">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={DEFAULT_CENTER}
          zoom={13}
          onLoad={map => setMapRef(map)}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            gestureHandling: 'cooperative',
            zoomControl: true,
            mapTypeId: 'roadmap',
            ...(mapRestrictions?.enabled && {
              restriction: {
                latLngBounds: mapRestrictions.bounds,
                strictBounds: mapRestrictions.strictBounds,
              },
            }),
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'poi.business',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
              {
                featureType: 'transit',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          }}
        />
      </div>

      <div className="bg-white p-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="font-semibold mb-2 text-sm">Heatmap Legend</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ background: legendSegments.low }} />
                <span>Low concentration</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ background: legendSegments.medium }} />
                <span>Medium concentration</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded" style={{ background: legendSegments.high }} />
                <span>High concentration</span>
              </div>
              <div className="text-gray-500 mt-1">Radius: {heatmapRadius}m</div>
            </div>
          </div>

          <div>
            <div className="font-semibold mb-2 text-sm">Heatmap Information</div>
            <div className="text-xs text-gray-600">
              Color intensity indicates incident density in the area. Uses a canvas overlay (Google
              HeatmapLayer was deprecated in Maps API 3.65+).
            </div>
          </div>

          <div>
            <div className="font-semibold mb-2 text-sm">Status & Data</div>
            <div className="space-y-1 text-xs text-gray-600">
              <div className="text-green-600">✓ Canvas heatmap active</div>
              <div>Total incidents: {filteredReports.length}</div>
              <div>With coordinates: {heatmapPoints.length}</div>
              <div>Radius: {heatmapRadius}m</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function parseReportHasCoordinates(report) {
  const c = report.coordinates;
  if (!c) return false;
  if (typeof c === 'object' && c.lat != null && c.lng != null) return true;
  if (typeof c === 'string' && c.trim() !== '') return true;
  return false;
}

export default IncidentHeatmap;
